import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/coqui-ai.github.io/coqui-ai.github.io/src/templates/BlogTemplate.tsx";
import { graphql } from 'gatsby';
export const pageQuery = graphql`
  query($fileAbsolutePath: String) {
    ...SidebarPageFragment
  }
`;
export const _frontmatter = {};
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`The Machine Learning team continues work on Coqui STT, an automatic speech recognition (ASR) engine which aims to make
speech recognition technology and trained models openly available to developers. Coqui STT is a deep learning-based ASR
engine with a simple API. We also provide pre-trained English models.`}</p>
    <p>{`Our latest release, version v0.6, offers the highest quality, most feature-packed model so far. In this overview, we’ll
show how Coqui STT can transform your applications by enabling client-side, low-latency, and privacy-preserving speech
recognition capabilities.`}</p>
    <h3 {...{
      "id": "consistent-low-latency",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#consistent-low-latency",
        "aria-label": "consistent low latency permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Consistent low latency`}</h3>
    <p>{`Coqui STT v0.6 includes a host of performance optimizations, designed to make it easier for application developers to
use the engine without having to fine tune their systems. Our new streaming decoder offers the largest improvement,
which means Coqui STT now offers consistent low latency and memory utilization, regardless of the length of the audio
being transcribed. Application developers can obtain partial transcripts without worrying about big latency spikes.`}</p>
    <p>{`Coqui STT is composed of two main subsystems: an acoustic model and a decoder. The acoustic model is a deep neural
network that receives audio features as inputs, and outputs character probabilities. The decoder uses a beam search
algorithm to transform the character probabilities into textual transcripts that are then returned by the system.`}</p>
    <p>{`In a `}<a parentName="p" {...{
        "href": "/blog/stt/speech-recognition-deepspeech"
      }}>{`previous blog post`}</a>{`, I discussed how we made the acoustic model
streamable. With both systems now capable of streaming, there’s no longer any need for carefully tuned silence detection
algorithms in applications. `}<a parentName="p" {...{
        "href": "https://github.com/dabinat"
      }}>{`dabinat`}</a>{`, a long-term volunteer contributor to the Coqui STT
code base, contributed this feature. Thanks!`}</p>
    <p>{`In the following diagram, you can see the same audio file being processed in real time by Coqui STT, before and after
the decoder optimizations. The program requests an intermediate transcription roughly every second while the audio is
being transcribed. The dotted black line marks when the program has received the final transcription. Then, the distance
from the end of the audio signal to the dotted line represents how long a user must wait after they’ve stopped speaking
until the final transcript is computed and the application is able to respond.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1000px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAABcRAAAXEQHKJvM/AAACBElEQVQoz32T63KbMBCFef/36Rv0Fk8y6aSp41twMBiDDQiExE3A1yXpTH+1Z0ajRVqdvZzFs8GROvCp6pqqLNFaY62FeaapNbqqqK5HVHahUgqtCt7hNPR/bMHc3nAmxnt7XvH69J0wOhMGAVEYvhMvGPoe50aG6sTYlrjB4frug6FLoTn/JdQ+fb7BS8uE8y3k185nE4SsdweO54SittyfFKvI8Hn9yuaScxfWfDl+nCWSsS5CcuG3C2N/Zshe8LqmlSwctlA02x2tlFv7PiY4oSWAOV04hy9UWUrph5RBhAoS8u2KPNvRXQv6IEavPpElj3j9UtY4UhVyIauutPQspZWyizjGZhnB6SDniuTtSFcqqlTI85j4Folfhb1lxMcNyuZ4szT/X+i6lqLIMSKUkmCFCGJMzf/g6abDdo7M9OSNo7DLPlC2jqsZKNqJa91T95PYI5n45HagcRO9CNaNE05y6oaBcRrxbrolqTru9lfufp54ChU/9gnrt4zHQ8rDNubrPmMTKe63Cd/WZx72KZerjJMELZShqQxOSh+nWUTpOqZ5YpDS5tcDY2MZwjf6S0QTBYyytLoxGy1i7ZjTC81yH4c0TUOfyLd/oHz+KYQT3iiCTGIYUXuQHljZrQjVivJGelgaiy9ipNcbR5nRpbxKJqGXN8sP0Ilf44b3Nwt+A6Qn+HYezBrAAAAAAElFTkSuQmCC')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": "IMAGE",
          "title": "IMAGE",
          "src": "/static/6b9f563c961d7e0a23c19ddf5d944531/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png",
          "srcSet": ["/static/6b9f563c961d7e0a23c19ddf5d944531/43fa5/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 250w", "/static/6b9f563c961d7e0a23c19ddf5d944531/c6e3d/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 500w", "/static/6b9f563c961d7e0a23c19ddf5d944531/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 1000w", "/static/6b9f563c961d7e0a23c19ddf5d944531/2e9ed/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 1500w", "/static/6b9f563c961d7e0a23c19ddf5d944531/9fabd/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 2000w", "/static/6b9f563c961d7e0a23c19ddf5d944531/6f79e/blog-stt-deepspeech-0-6-speech-to-text-engine-plots.png 2100w"],
          "sizes": "(max-width: 1000px) 100vw, 1000px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
    <p>{`In this case, the latest version of Coqui STT provides the transcription 260ms after the end of the audio, which is 73%
faster than before the streaming decoder was implemented. This difference would be even larger for a longer recording.
The intermediate transcript requests at seconds 2 and 3 of the audio file are also returned in a fraction of the time.`}</p>
    <p>{`Maintaining low latency is crucial for keeping users engaged and satisfied with your application. Coqui STT enables
low-latency speech recognition services regardless of network conditions, as it can run offline, on users’ devices.`}</p>
    <h3 {...{
      "id": "tensorflow-lite-smaller-models-faster-start-up-times",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#tensorflow-lite-smaller-models-faster-start-up-times",
        "aria-label": "tensorflow lite smaller models faster start up times permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`TensorFlow Lite, smaller models, faster start-up times`}</h3>
    <p>{`We have added support for `}<a parentName="p" {...{
        "href": "https://www.tensorflow.org/lite"
      }}>{`TensorFlow Lite`}</a>{`, a version of TensorFlow that’s optimized
for mobile and embedded devices. This has reduced the Coqui STT package size from 98 MB to 3.7 MB. It has reduced our
English model size from 188 MB to 47 MB. We did this via `}<a parentName="p" {...{
        "href": "https://www.tensorflow.org/lite/performance/post_training_quantization"
      }}>{`post-training
quantization`}</a>{`, a technique to compress model
weights after training is done. TensorFlow Lite is designed for mobile and embedded devices, but we found that for Coqui
STT it is even faster on desktop platforms. And so, we’ve made it available on Windows, macOS, and Linux as well as
Raspberry Pi and Android. `}<strong parentName="p">{`Coqui STT v0.6 with TensorFlow Lite runs faster than real time on a single core of a
Raspberry Pi 4.`}</strong></p>
    <p>{`The following diagram compares the start-up time and peak memory utilization for Coqui STT versions v0.4.1, v0.5.1, and
our latest release, v0.6.0.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1000px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "43.2%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAC4jAAAuIwF4pT92AAAB3UlEQVQoz3WSXWsTQRSG99+11gs/4o2U1q9iI3qjguB/8K4aRbBemlaxeiWI4oUg0koSepGWljYNyWa/0/3KZjebzePsbGJBcWBmDgfmmfe85yi6bjAY+FQqHkvLNj+qLzj9ushA3SGIEnStg67rdDpdDMPh2yeL1rHKycmxyHXo9Xoib6CqKrZto1iWTZIElEopigIfHy/Cpgi6W3z/CWtrGr++fEbffoLd2qFb36bTOuLg8JB2uy2hpmlKsOM4KLnCOPaZm8sk8P39S1AVgfaGpRvI3Hp5Fd4pjJtPgYw0DhgOYyEkEfcQz/PwfZ8wDM+A5xcK4NbDK1PgBtduFcBX5TJsKIyaz5kIJJMxszWZTCTQdV2CIMhLtsRPPgvnpsAHpX+A6zlQ2JA2KxI4yUYSlO8sy2Spuc/9fh/FMMz/Kry+MgXe+Qs4TiRMRAI4/qMwiqKzkufnZx5eLoB6leWb05JXb0+Bz2aFyjPJYCy2I7qraZr0U3FdTwQeFy+M5OMPj64WXe5tslIugK/v3YW3wsO9l4QjMQDdLla7gdqo4mp7BOFQjF4oP1EKM33hQUqtZrO/WyM5PSAe9MV8pdQbfZHbJbKaxKEju2uaNkf7NTy1Tho5uJ4vVebrNwEyWth5Kr8iAAAAAElFTkSuQmCC')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": "IMAGE",
          "title": "IMAGE",
          "src": "/static/acc57edc910edd3f5d44cdbd37143f05/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png",
          "srcSet": ["/static/acc57edc910edd3f5d44cdbd37143f05/43fa5/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 250w", "/static/acc57edc910edd3f5d44cdbd37143f05/c6e3d/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 500w", "/static/acc57edc910edd3f5d44cdbd37143f05/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 1000w", "/static/acc57edc910edd3f5d44cdbd37143f05/2e9ed/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 1500w", "/static/acc57edc910edd3f5d44cdbd37143f05/9fabd/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 2000w", "/static/acc57edc910edd3f5d44cdbd37143f05/df6b6/blog-stt-deepspeech-0-6-speech-to-text-engine-startup-memory.png 2471w"],
          "sizes": "(max-width: 1000px) 100vw, 1000px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
    <p>{`We now use `}<strong parentName="p">{`22 times less memory`}</strong>{` and start up over `}<strong parentName="p">{`500 times faster`}</strong>{`. Together with the optimizations we’ve
applied to our language model, a complete Coqui STT package including the inference code and a trained English model is
now more than `}<strong parentName="p">{`50% smaller`}</strong>{`.`}</p>
    <h3 {...{
      "id": "confidence-value-and-timing-metadata-in-the-api",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#confidence-value-and-timing-metadata-in-the-api",
        "aria-label": "confidence value and timing metadata in the api permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Confidence value and timing metadata in the API`}</h3>
    <p>{`In addition, the new decoder exposes timing and confidence metadata, providing new possibilities for applications. We
now offer an extended set of functions in the API, not just the textual transcript. You also get metadata timing
information for each character in the transcript, and a per-sentence confidence value.`}</p>
    <p>{`The example below shows the timing metadata extracted from Coqui STT from a sample audio file. The per-character timing
returned by the API is grouped into word timings. You can see the waveform below. Click the “Play” button to listen to
the sample.`}</p>
    <iframe width="100%" height="300" src="//jsfiddle.net/fwd9peL5/5/embedded/result/" allowFullScreen="allowfullscreen" allowpaymentrequest frameBorder="0"></iframe>
    <p><a parentName="p" {...{
        "href": "https://tehiku.nz/"
      }}>{`Te Hiku Media`}</a>{` are using Coqui STT to develop and deploy the first `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/M%C4%81ori_language"
      }}>{`Te reo
Māori`}</a>{` automatic speech recognizer. They have been exploring the use
of the confidence metadata in our new decoder to build a digital pronunciation helper for Te reo Māori. Recently, they
received a $13 million NZD investment from New Zealand’s Strategic Science Investment Fund to build `}<a parentName="p" {...{
        "href": "https://tehiku.nz/te-hiku-tech/papa-reo/"
      }}>{`Papa Reo, a
multilingual language platform`}</a>{`. They are starting with New Zealand English
and Te reo Māori.`}</p>
    <h3 {...{
      "id": "windowsnet-support",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#windowsnet-support",
        "aria-label": "windowsnet support permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Windows/.NET support`}</h3>
    <p>{`Coqui STT v0.6 now offers packages for Windows, with .NET, Python, JavaScript, and C bindings. Windows support was a
much-requested feature that was contributed by `}<a parentName="p" {...{
        "href": "https://github.com/carlfm01"
      }}>{`Carlos Fonseca`}</a>{`, who also wrote the .NET
bindings and examples. Thanks Carlos!`}</p>
    <p>{`You can find more details about our Windows support by looking at `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/tree/v0.6.0/examples/net_framework/DeepSpeechWPF"
      }}>{`the WPF
example`}</a>{` (pictured below). It uses the
.NET bindings to create a small UI around Coqui STT. Our .NET package is available in the `}<a parentName="p" {...{
        "href": "https://www.nuget.org/packages/STT"
      }}>{`NuGet
Gallery`}</a>{`. You can install it directly from Visual Studio.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1000px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "73.2%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAPCAYAAADkmO9VAAAACXBIWXMAABYlAAAWJQFJUiTwAAAB6UlEQVQ4y62TW4/TMBCF8/9/EC9IIB7QIvFQpC2720tIEyfNtbm6ju1cDmOnRbDqsiuEpU9jK5PJzPGJ8/5HgSTLEWQlGFEJDT7MaPWE7hVaOaKRA/JWgEuNx7SDs85aQHUY+zNG0QGjAgYJYMLb15JbnhWcL6xEcyrAjjGCMLLdllWNYVySxmmG0gPUMP6VeZrgVz2cD08h9g9rbLY7bIn7+zWCIABjzJ7zPMdEycMwvIimD2IawSoB584vkEUMcZIiy1IwKpYkicX3fdR1/erA87zEuNNwntrpDS/MNzGdL3GpeGxJw1XKAd1D9BJCCJvwr8t2+PVY4+h72O1dYm/HjOmCoihCHMdomgZt2/6BkcE8c13XxrbrIHgHL2+XDnlZoKKkuqltsqGqKluMc36TsixtMRPPNJmWAv6JL7YxBaXSlxvTUJe9lNLGq05GMxOfy3LVMGoknI/bCMzdwfUO8DyPrLLFev0dm80Gq9U32+WtS1qKL4zTcrGsJtt82seIScODH1j9wjD85cPD4WDtk6UpiqIgbY/IyJfPO7yew4aMvcoF/Q4aPY1pxtVm7IthlRmfkEpZGXqSQNF+Mh3+xngZmdVU8KFS+F8r6UjDd36DoOLYFBy70wsUV7qbbHOyDOV99iv8BAJEg7lqBCnNAAAAAElFTkSuQmCC')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": "IMAGE",
          "title": "IMAGE",
          "src": "/static/6ea80cc6ad61e741d4b979637b3193d5/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-screenshot.png",
          "srcSet": ["/static/6ea80cc6ad61e741d4b979637b3193d5/43fa5/blog-stt-deepspeech-0-6-speech-to-text-engine-screenshot.png 250w", "/static/6ea80cc6ad61e741d4b979637b3193d5/c6e3d/blog-stt-deepspeech-0-6-speech-to-text-engine-screenshot.png 500w", "/static/6ea80cc6ad61e741d4b979637b3193d5/da8b6/blog-stt-deepspeech-0-6-speech-to-text-engine-screenshot.png 1000w", "/static/6ea80cc6ad61e741d4b979637b3193d5/d8661/blog-stt-deepspeech-0-6-speech-to-text-engine-screenshot.png 1238w"],
          "sizes": "(max-width: 1000px) 100vw, 1000px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
    <p>{`You can see the WPF example that’s available in our repository. It contains code demonstrating transcription from an
audio file, and also from a microphone or other audio input device.`}</p>
    <h3 {...{
      "id": "centralized-documentation",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#centralized-documentation",
        "aria-label": "centralized documentation permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Centralized documentation`}</h3>
    <p>{`We have centralized the documentation for all our language bindings in a single website,
`}<a parentName="p" {...{
        "href": "https://stt.readthedocs.io/"
      }}>{`stt.readthedocs.io`}</a>{`. You can find the documentation for C, Python, .NET, Java and
NodeJS/Electron packages. Given the variety of language bindings available, we wanted to make it easier to locate the
correct documentation for your platform.`}</p>
    <h3 {...{
      "id": "improvements-for-training-models",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#improvements-for-training-models",
        "aria-label": "improvements for training models permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Improvements for training models`}</h3>
    <p>{`With the upgrade to TensorFlow 1.14, we now leverage the `}<a parentName="p" {...{
        "href": "https://www.tensorflow.org/versions/r1.14/api_docs/python/tf/contrib/cudnn_rnn/CudnnLSTM"
      }}>{`CuDNN RNN
APIs`}</a>{` for our training code.
This change gives us around 2x faster training times, which means faster experimentation and better models.`}</p>
    <p>{`Along with faster training, we now also support online feature augmentation, as described in Google’s `}<a parentName="p" {...{
        "href": "https://arxiv.org/abs/1904.08779"
      }}>{`SpecAugment
paper`}</a>{`. This feature was contributed by `}<a parentName="p" {...{
        "href": "https://iarahealth.com/"
      }}>{`Iara Health`}</a>{`, a
Brazilian startup providing transcription services for health professionals. Iara Health has used online augmentation to
improve their production Coqui STT models.`}</p>
    <div align="center">
  {' '}
  <iframe width="560" height="315" src="https://www.youtube.com/embed/uNqARK7euXk" frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>{' '}
    </div>
    <p>{`The video above shows a customer using the Iara Health system. By using voice commands and dictation, the user instructs
the program to load a template. Then, while looking at results of an MRI scan, they dictate their findings. The user can
complete the report without typing. Iara Health has trained their own Brazilian Portuguese models for this specialized
use case.`}</p>
    <h3 {...{
      "id": "try-out-coqui-stt-v06",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#try-out-coqui-stt-v06",
        "aria-label": "try out coqui stt v06 permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`Try out Coqui STT v0.6`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/releases/v0.6.0"
      }}>{`Coqui STT v0.6 release`}</a>{` includes our speech recognition engine as
well as a trained English model. We provide binaries for six platforms and, as mentioned above, have bindings to various
programming languages, including Python, JavaScript, Go, Java, and .NET.`}</p>
    <p>{`The included English model was trained on 3816 hours of transcribed audio coming from `}<a parentName="p" {...{
        "href": "https://voice.mozilla.org/en/datasets"
      }}>{`Common Voice
English`}</a>{`, `}<a parentName="p" {...{
        "href": "http://www.openslr.org/12"
      }}>{`LibriSpeech`}</a>{`,
`}<a parentName="p" {...{
        "href": "https://www.ldc.upenn.edu/sites/www.ldc.upenn.edu/files/lrec2004-fisher-corpus.pdf"
      }}>{`Fisher`}</a>{`,
`}<a parentName="p" {...{
        "href": "https://catalog.ldc.upenn.edu/LDC97S62"
      }}>{`Switchboard`}</a>{`. The model also includes around 1700 hours of transcribed WAMU
(NPR) radio shows. It achieves a 7.5% word error rate on the LibriSpeech test clean benchmark, and is faster than real
time on a single core of a Raspberry Pi 4.`}</p>
    <p>{`Coqui STT v0.6 includes our best English model yet. However, most of the data used to train it is American English. For
this reason, it doesn’t perform as well as it could on other English dialects and accents. A lack of publicly available
voice data in other languages and dialects is part of why `}<a parentName="p" {...{
        "href": "https://voice.mozilla.org/"
      }}>{`Common Voice`}</a>{` was created. We
want to build a future where a speaker of Welsh or Basque or Scottish English has access to speech technology with the
same standard of quality as is currently available for speakers of languages with big markets like American English,
German, or Mandarin.`}</p>
    <p>{`Want to participate in Common Voice? You can donate your voice by reading small text fragments. Or validate existing
recordings in 40 different languages, with more to come. Currently, Common Voice represents the world’s largest public
domain transcribed `}<a parentName="p" {...{
        "href": "https://voice.mozilla.org/en/datasets"
      }}>{`voice dataset`}</a>{`. The dataset consists of nearly 2,400 hours of
voice data with 29 languages represented, including English, French, German, Spanish and Mandarin Chinese, but also for
example Welsh and Kabyle.`}</p>
    <p>{`The v0.6 release is now available on `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/releases/v0.6.0"
      }}>{`GitHub`}</a>{` as well as on your
favorite package manager. You can download our pre-trained model and start using Coqui STT in minutes. If you’d like to
know more, you can find detailed release notes in the `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/releases/v0.6.0"
      }}>{`GitHub release`}</a>{`;
installation and usage explanations in our `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/blob/v0.6.0/README.rst"
      }}>{`README`}</a>{`. If that
doesn’t cover what you’re looking for, you can also use our `}<a parentName="p" {...{
        "href": "https://github.com/coqui-ai/STT/discussions"
      }}>{`discussion
forum`}</a>{`.`}</p>
    <h3 {...{
      "id": "license",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h3" {...{
        "href": "#license",
        "aria-label": "license permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "xmlns": "http://www.w3.org/2000/svg",
          "width": "16",
          "height": "16",
          "focusable": "false",
          "viewBox": "0 0 16 16"
        }}>{`
  `}<path parentName="svg" {...{
            "fill": "currentColor",
            "d": "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
          }}></path>
        </svg></a>{`License`}</h3>
    <p><a parentName="p" {...{
        "href": "https://creativecommons.org/licenses/by-sa/3.0/"
      }}>{`Creative Commons Attribution Share-Alike License v3.0`}</a>{` or any later
version`}</p>


    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      